import React, { useState, useEffect } from "react";
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
     
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };
  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// SupplierWise GST Report Display Component
const SupplierWiseGSTReportDisplay = ({ formData, filters, branchProfile, purchaseData }) => {
  // Helper function to parse date
  const parseInvoiceDate = (dateString) => {
    if (!dateString) return null;
    // Handle DD-MM-YYYY or DD/MM/YYYY
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    // Handle YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    // Fallback to default Date parsing
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseInvoiceDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter data based on date range
  let filteredPurchaseData = purchaseData;
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    fromDate.setHours(0, 0, 0, 0); // Start of the selected from date
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999); // End of the selected to date
    filteredPurchaseData = filteredPurchaseData.filter(item => {
      const itemDate = parseInvoiceDate(item.invoiceDate);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  // Apply filters
  if (!filters.supplier.includes("All Suppliers")) {
    filteredPurchaseData = filteredPurchaseData.filter(item =>
      filters.supplier.includes(item.supplier)
    );
  }

  if (!filters.group.includes("All Groups")) {
    filteredPurchaseData = filteredPurchaseData.filter(item =>
      filters.group.includes(item.groupName)
    );
  }

  if (!filters.product.includes("All Products")) {
    filteredPurchaseData = filteredPurchaseData.filter(item =>
      filters.product.includes(item.productName)
    );
  }

  // Initialize displayData
  let displayData = [];

  // Process data based on report type
  if (formData.reportType === "abstract") {
    // Abstract report - group by supplier
    const groupedData = filteredPurchaseData.reduce((acc, item) => {
      const key = item.supplier;
      if (!acc[key]) {
        acc[key] = {
          supplier: key,
          mrnNo: item.mrnNo || 'N/A',
          mrnDate: item.invoiceDate || '',
          invNo: item.invoiceNo || 'N/A',
          invDate: item.invoiceDate || '',
          gstin: item.gstin || 'N/A',
          cgstAmt: 0,
          sgstAmt: 0,
          igstAmt: 0,
          gstAmt: 0,
          cessAmt: 0,
          grossAmt: 0,
          discAmt: 0,
          netValue: 0,
          count: 0
        };
      }
      acc[key].cgstAmt += parseFloat(item.cgstAmt) || 0;
      acc[key].sgstAmt += parseFloat(item.sgstAmt) || 0;
      acc[key].igstAmt += parseFloat(item.igstAmt) || 0;
      acc[key].gstAmt += parseFloat(item.gstAmt) || 0;
      acc[key].cessAmt += parseFloat(item.cessAmt) || 0;
      acc[key].grossAmt += parseFloat(item.grossAmt) || 0;
      acc[key].discAmt += parseFloat(item.discAmt) || 0;
      acc[key].netValue += parseFloat(item.netValue) || 0;
      acc[key].count += 1;
      return acc;
    }, {});

    displayData = Object.values(groupedData);
    displayData.sort((a, b) => a.supplier.localeCompare(b.supplier));
  } else {
    // Detail report - use filtered data directly
    displayData = [...filteredPurchaseData];
    displayData.sort((a, b) => {
      return a.supplier.localeCompare(b.supplier) || 
             parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
    });
  }

  // Calculate totals
  const totalQty = displayData.reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0);
  const totalGrossAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.grossAmt) || 0), 0);
  const totalDiscAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0);
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || 0), 0);
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.sgstAmt) || 0), 0);
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.igstAmt) || 0), 0);
  const totalCessAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cessAmt) || 0), 0);
  const totalGstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.gstAmt) || 0), 0);
  const totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netValue) || 0), 0);

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  // Render table headers
  const renderTableHeaders = () => {
    if (formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GSTIN</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>

          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>PRate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>IGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Cess Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier Name</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GSTIN</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice Count</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>IGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Cess Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    }
  };

  // Render table rows
  const renderTableRows = () => {
    if (displayData.length === 0) {
      const colSpan = formData.reportType === "detail" ? 19 : 13;
      return (
        <tr>
          <td colSpan={colSpan} style={{
            border: '1px solid #cbd5e1',
            padding: '30px',
            textAlign: 'center',
            color: '#64748b',
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No purchase data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => {
      if (formData.reportType === "abstract") {
        // Abstract view - supplier summary
        const supplierTotalQty = filteredPurchaseData
          .filter(p => p.supplier === item.supplier)
          .reduce((sum, p) => sum + (parseFloat(p.qty) || 0), 0);
        
        return (
          <tr key={item.supplier} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.gstin}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.count}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{supplierTotalQty.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.grossAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.discAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.cgstAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.sgstAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.igstAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.gstAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.cessAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.netValue.toFixed(2)}</td>
          </tr>
        );
      } else {
        // Detail view - individual records
        return (
          <tr key={`${item.invoiceId}-${item.productName}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invoiceNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.invoiceDate)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.gstin || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>

            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.pRate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.grossAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.sgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.igstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.gstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cessAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      }
    });
  };

  // Render total row
  const renderTotalRow = () => {
    if (displayData.length === 0) return null;
    
    if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalIgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalIgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0',
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              Supplierwise Purchase GST Report
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : 'N/A'}</span>
              <span><strong>To:</strong> {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : 'N/A'}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
          </div>
        </div>
      </div>

      {/* Purchase Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '11px',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#0891b2' }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total SGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#c2410c' }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total IGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalIgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CESS</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalCessAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Gross Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{totalGrossAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Purchase Supplier Wise GST Component
const PurchaseSupplierWiseGST = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("Supplierwise");
  const [reportType, setReportType] = useState("detail");
  const [filters, setFilters] = useState({
    supplier: ["All Suppliers"],
    group: ["All Groups"],
    product: ["All Products"]
  });
  const [suppliers, setSuppliers] = useState(["All Suppliers"]);
  const [groups, setGroups] = useState(["All Groups"]);
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [purchaseData, setPurchaseData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [groupsMap, setGroupsMap] = useState({});
  const [suppliersMap, setSuppliersMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);
    
    // Load branch profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
      }
    });

    // Load suppliers
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    onValue(suppliersRef, (snapshot) => {
      const suppliersData = ["All Suppliers"];
      const suppliersMapLocal = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const supplier = childSnapshot.val();
          if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
            suppliersData.push(supplier.supplierName);
            suppliersMapLocal[supplier.supplierName] = supplier;
          }
        });
      }
      setSuppliers(suppliersData);
      setSuppliersMap(suppliersMapLocal);
    });

    // Load groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Groups"];
      const groupsMapping = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
            groupsMapping[childSnapshot.key] = group.name;
            groupsMapping[group.name] = group.name;
          }
        });
      }
      setGroups(groupsData);
      setGroupsMap(groupsMapping);
     
      loadProductsWithGroups(encodedEmail, groupsMapping);
    });

    return () => {
      off(branchProfileRef);
      off(suppliersRef);
      off(groupsRef);
    };
  };

  const loadProductsWithGroups = (encodedEmail, groupsMapping) => {
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
           
            let groupName = 'No Group Assigned';
           
            if (product.groupName && product.groupName !== '') {
              groupName = product.groupName;
            } else if (product.group && groupsMapping[product.group]) {
              groupName = groupsMapping[product.group];
            } else if (product.group && Object.values(groupsMapping).includes(product.group)) {
              groupName = product.group;
            }
           
            groupMap[product.productName] = groupName;
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
     
      loadInvoices(encodedEmail, groupMap);
    });
  };


const loadInvoices = (encodedEmail, groupMap) => {
  const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
  onValue(invoicesRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const invoiceRecords = [];
      Object.entries(data).forEach(([invoiceId, invoice]) => {
        if (invoice.items && Array.isArray(invoice.items)) {
          const supplierName = invoice.supplierDetails?.supplier || "Unknown Supplier";
          const supplierGSTIN = suppliersMap[supplierName]?.gstTinNo || 'N/A';
          
          // Check if IGST is applied based on the invoice summary
          const isIgstApplied = invoice.summary?.igstAmt > 0;

          invoice.items.forEach((item) => {
            if (item.item && item.qty > 0) {
              const qty = parseFloat(item.qty) || 0;
              const pRate = parseFloat(item.pRate) || 0;
              const discAmt = parseFloat(item.discAmt) || 0;
              const grossAmt = qty * pRate;
              const taxableValue = grossAmt - discAmt;

              // Get GST amounts directly from the item
              const cgstAmt = parseFloat(item.cgstAmt) || 0;
              const sgstAmt = parseFloat(item.sgstAmt) || 0;
              const igstAmt = parseFloat(item.igstAmt) || 0;
              const cessAmt = parseFloat(item.cessAmt) || 0;
              
              // Calculate total GST amount
              const totalGstAmt = cgstAmt + sgstAmt + igstAmt;
              
              // Calculate net value including all taxes
              const netValue = taxableValue + totalGstAmt + cessAmt;

              const groupName = groupMap[item.item] || 'No Group Assigned';

              invoiceRecords.push({
                invoiceId: invoiceId,
                invoiceNo: invoice.supplierDetails?.invoiceNo || 'N/A',
                invoiceDate: invoice.supplierDetails?.invoiceDate || "",
                supplier: supplierName,
                gstin: supplierGSTIN,
                productName: item.item || "Unknown Product",
                groupName: groupName,
                batch: item.batch || "",
                eDate: item.eDate || "",
                mrnNo: invoice.supplierDetails?.mrnNo || 'N/A',
                qty: qty,
                pRate: pRate,
                grossAmt: grossAmt,
                discAmt: discAmt,
                cgstAmt: cgstAmt,
                sgstAmt: sgstAmt,
                igstAmt: igstAmt,
                cessAmt: cessAmt,
                gstAmt: totalGstAmt,
                netValue: netValue,
                // Add GST percentages for reference
                cgstPercent: parseFloat(item.cgstPercent) || 0,
                sgstPercent: parseFloat(item.sgstPercent) || 0,
                igstPercent: parseFloat(item.igstPercent) || 0,
                cessPercent: parseFloat(item.cessPercent) || 0
              });
            }
          });
        }
      });
      setPurchaseData(invoiceRecords);
    } else {
      setPurchaseData([]);
    }
  });
};


  // Add useEffect to reload invoices when suppliersMap changes
  useEffect(() => {
    if (currentUser && Object.keys(suppliersMap).length > 0) {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      loadInvoices(encodedEmail, productGroupMap);
    }
  }, [suppliersMap, currentUser, productGroupMap]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  const handlePrint = () => {
    const printContent = (
      <SupplierWiseGSTReportDisplay
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        purchaseData={purchaseData}
      />
    );
    const htmlString = ReactDOMServer.renderToString(printContent);
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Supplierwise GST Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
                 padding: 4px !important;
            font-size: 7px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 60px;
            }
            /* Specific column widths for better fit */
            th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
            th:nth-child(2), td:nth-child(2) { width: 50px; } /* Invoice No */
            th:nth-child(3), td:nth-child(3) { width: 50px; } /* Invoice Date */
            th:nth-child(4), td:nth-child(4) { width: 80px; } /* Supplier */
            th:nth-child(5), td:nth-child(5) { width: 70px; } /* GSTIN */
            th:nth-child(6), td:nth-child(6) { width: 80px; } /* Product Name */
            th:nth-child(7), td:nth-child(7) { width: 40px; } /* Qty */
            th:nth-child(8), td:nth-child(8) { width: 40px; } /* PRate */
            th:nth-child(9), td:nth-child(9) { width: 50px; } /* Gross Amt */
            th:nth-child(10), td:nth-child(10) { width: 45px; } /* Disc.Amt */
            th:nth-child(11), td:nth-child(11) { width: 50px; } /* CGST */
            th:nth-child(12), td:nth-child(12) { width: 50px; } /* SGST */
            th:nth-child(13), td:nth-child(13) { width: 50px; } /* IGST */
            th:nth-child(14), td:nth-child(14) { width: 50px; } /* GST Amt */
            th:nth-child(15), td:nth-child(15) { width: 45px; } /* Cess */
            th:nth-child(16), td:nth-child(16) { width: 60px; font-weight: bold; } /* Net Value */
            
            /* Abstract report column widths */
            .abstract th:nth-child(1), .abstract td:nth-child(1) { width: 30px; } /* S.No */
            .abstract th:nth-child(2), .abstract td:nth-child(2) { width: 120px; } /* Supplier */
            .abstract th:nth-child(3), .abstract td:nth-child(3) { width: 80px; } /* GSTIN */
            .abstract th:nth-child(4), .abstract td:nth-child(4) { width: 50px; } /* Count */
            .abstract th:nth-child(5), .abstract td:nth-child(5) { width: 50px; } /* Qty */
            .abstract th:nth-child(6), .abstract td:nth-child(6) { width: 60px; } /* Gross */
            .abstract th:nth-child(7), .abstract td:nth-child(7) { width: 50px; } /* Disc */
            .abstract th:nth-child(8), .abstract td:nth-child(8) { width: 50px; } /* CGST */
            .abstract th:nth-child(9), .abstract td:nth-child(9) { width: 50px; } /* SGST */
            .abstract th:nth-child(10), .abstract td:nth-child(10) { width: 50px; } /* IGST */
            .abstract th:nth-child(11), .abstract td:nth-child(11) { width: 50px; } /* GST */
            .abstract th:nth-child(12), .abstract td:nth-child(12) { width: 50px; } /* Cess */
            .abstract th:nth-child(13), .abstract td:nth-child(13) { width: 70px; font-weight: bold; } /* Net Value */
            
            .print-only {
              width: 93% !important;
              max-width: none !important;
              overflow: visible !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };
  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Purchase Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        color: '#64748b',
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Supplierwise GST Report Generator</p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
         
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
     
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Supplierwise (GSTIN)_1A</h1>
      </div>
     
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="Supplierwise">Supplierwise</option>
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="detail">Detail</option>
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <FilterSection
          title="Supplier Name"
          options={suppliers}
          selected={filters.supplier}
          onChange={(value) => handleFilterChange("supplier", value)}
        />
        <FilterSection
          title="Group Name"
          options={groups}
          selected={filters.group}
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection
          title="Product Name"
          options={products}
          selected={filters.product}
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Report Display */}
      {showReport && (
        <SupplierWiseGSTReportDisplay
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          purchaseData={purchaseData}
        />
      )}
    </div>
  );
};

export default PurchaseSupplierWiseGST;